/** In-page camera QR scanner for the till.
 *
 * The LSK Web Extensions API has no camera-open call — native scans arrive via
 * `pos_onBarcodeScanEvent` / `pos_onNFCEvent`. This is the active alternative: it opens the
 * device camera with the standard web `getUserMedia` API and decodes QR codes in-page.
 *
 * Decoder: the native `BarcodeDetector` API when present (Chromium web views), otherwise the
 * `jsqr` fallback. On the LSK iPad (WKWebView) `BarcodeDetector` is unavailable, so jsQR is the
 * path that actually runs there — confirmed working on a real device. jsqr is dynamically
 * imported so it is only fetched when the fallback is needed (and never on BarcodeDetector
 * hosts), keeping it out of the initial bundle.
 *
 * Requires a secure context (HTTPS — Firebase Hosting) and the host web view granting camera
 * permission. Failures surface a readable message rather than throwing.
 */
import { useEffect, useRef, useState } from 'react'
import { t } from '../i18n'
// Decoder helpers live in their own module so this file only exports a component
// (react-doctor: only-export-components — keeps React Fast Refresh working).
import { cameraErrorMessage, createBarcodeDetector } from './camera-utils'

type Status = 'starting' | 'scanning' | 'error'

interface CameraScannerProps {
  /** Called once with the decoded QR value (trimmed, non-empty). The camera is stopped first. */
  onDecode: (value: string) => void
  /** Which camera to use (controlled by the parent's "Change camera" control). `facingMode` is a
   * preference, not `exact`, so single-camera devices just fall back to whatever they have. */
  facingMode: 'user' | 'environment'
}

/** Full-bleed camera preview that decodes QR codes and overlays the scan instruction.
 * Controls (change camera / cancel) live in the parent sidebar. */
export function CameraScanner({ onDecode, facingMode }: CameraScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [status, setStatus] = useState<Status>('starting')
  const [error, setError] = useState<string | null>(null)
  // Survives a camera switch (effect re-run) so a successful decode never fires twice.
  const decodedRef = useRef(false)
  // Latest `onDecode` in a ref so the camera effect only re-runs on `facingMode` (a camera
  // switch) — not every time the parent passes a fresh callback identity.
  const onDecodeRef = useRef(onDecode)
  useEffect(() => {
    onDecodeRef.current = onDecode
  }, [onDecode])

  useEffect(() => {
    let stream: MediaStream | null = null
    let timer: ReturnType<typeof setInterval> | null = null
    let cancelled = false
    const canvas = document.createElement('canvas')

    function stop() {
      if (timer) {
        clearInterval(timer)
        timer = null
      }
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
        stream = null
      }
    }

    function fail(message: string) {
      if (cancelled) return
      stop()
      setError(message)
      setStatus('error')
    }

    function handle(value: string) {
      if (decodedRef.current || cancelled) return
      const trimmed = value.trim()
      if (!trimmed) return
      decodedRef.current = true
      stop()
      onDecodeRef.current(trimmed)
    }

    async function run() {
      if (!window.isSecureContext) return fail(t('camInsecure'))
      const media = navigator.mediaDevices
      if (!media?.getUserMedia) return fail(t('camUnavailable'))

      setStatus('starting')
      try {
        stream = await media.getUserMedia({ video: { facingMode }, audio: false })
      } catch (e) {
        return fail(cameraErrorMessage(e))
      }
      if (cancelled) return stop()

      const video = videoRef.current
      if (!video) return stop()
      video.srcObject = stream
      await video.play().catch(() => {})

      // Build the per-frame decode step, choosing native detector or the jsQR fallback.
      let decodeFrame: () => Promise<void>
      const detector = createBarcodeDetector()
      if (detector) {
        decodeFrame = async () => {
          const codes = await detector.detect(video)
          if (codes[0]?.rawValue) handle(codes[0].rawValue)
        }
      } else {
        const { default: jsQR } = await import('jsqr')
        if (cancelled) return stop()
        const ctx = canvas.getContext('2d')
        if (!ctx) return fail(t('camCanvas'))
        decodeFrame = async () => {
          if (!video.videoWidth) return
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          const image = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const code = jsQR(image.data, image.width, image.height)
          if (code?.data) handle(code.data)
        }
      }

      setStatus('scanning')
      timer = setInterval(() => {
        void decodeFrame().catch(() => {
          /* transient per-frame decode error — keep scanning */
        })
      }, 250)
    }

    void run()
    return () => {
      cancelled = true
      stop()
    }
    // Re-runs only when `facingMode` changes (switch camera): cleanup stops the old stream, then
    // a fresh stream starts on the other camera. `onDecode` is read through a ref, so it isn't a
    // dependency — the dep list is exhaustive as written.
  }, [facingMode])

  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <video
        ref={videoRef}
        playsInline
        muted
        aria-label="Camera preview"
        className="h-full w-full object-cover"
      />
      {/* Scrim only. The scan instruction used to live here, from when the camera was its own
          full-bleed screen; ScanLobby now owns the reticle and captions, and rendering both put
          two instructions on the feed at once — one of them across the wordmark. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/45 to-transparent" />
      {status !== 'scanning' && (
        <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-base text-white">
          {status === 'error' ? (error ?? t('camError')) : t('startingCamera')}
        </div>
      )}
    </div>
  )
}
